// ─── Logbook Definition ─────────────────────────────────────────────────────

export type GeneratorVersion = "v2" | "old";

export interface V2LogbookDef {
  generator?: "v2";
  logbookName: string;
  logbookVersion: string;
  primaryVerifiers?: string[];
  secondaryVerifiers?: string[];
  /** Short text shown in place of primaryVerifiers list in summaries */
  primaryText?: string;
  /** Short text shown in place of secondaryVerifiers list in summaries */
  secondaryText?: string;
  /** 0 / false = not regulatory, 1 / true = regulatory */
  regulatory?: boolean | 0 | 1;
  author?: string;
  isReadonly?: boolean;
  features?: V2Features;
  content: V2ContentNode[];
}

// ─── Features ────────────────────────────────────────────────────────────────

export interface V2Features {
  /** Show a side panel listing group anchors */
  panel?: V2PanelFeature;
  [key: string]: Record<string, any> | undefined;
}

export interface V2PanelFeature {
  align?: "left" | "right" | "center";
}

// ─── Content Nodes ───────────────────────────────────────────────────────────

/** A content node is either a group (layout container) or an item (component) */
export type V2ContentNode = V2Group | V2Item;

// ─── Group ───────────────────────────────────────────────────────────────────

export interface V2Group {
  /** Enable add row operation for dynamic groups */
  addRow?: boolean;
  /** Enable copy row operation for dynamic groups */
  copyRow?: boolean;
  /** Enable delete row operation for dynamic groups */
  deleteRow?: boolean;
  /** Enable move row operation for dynamic groups */
  moveRow?: boolean;
  /** Unique group identifier (UUID or manual name). If absent / "auto", one is generated. */
  group: string;
  /** Mark cloned row instances so merge logic treats them as instantiated rows, not templates */
  rowInstance?: boolean;
  /** Internal runtime ID for tracking (added during preparation, removed before serialization) */
  id?: string;
  layout?: "vertical" | "horizontal";
  /** Panel ID – registers this group in the side panel with this anchor text */
  panel?: string;
  label?: V2LabelDef;
  inherited?: V2InheritedDef;
  content?: V2ContentNode[];

  // ── Layout / visual overrides ──────────────────────────────────────────────
  /** Group visual style variant */
  style?: "solid" | "clear" | "none" | string;
  /** CSS background color */
  backColor?: string;
  /** CSS border-radius for the group card */
  rounded?: string;
  /** CSS width */
  width?: string;
  /** CSS min-width */
  minWidth?: string;
  /** CSS min-height */
  minHeight?: string;
  /** CSS height */
  height?: string;
  /** CSS padding */
  padding?: string;
  /** CSS margin-top shorthand */
  marginTop?: string;
  /** CSS margin-bottom shorthand */
  marginBottom?: string;
  /** CSS class name(s) */
  class?: string;
}

// ─── Item ────────────────────────────────────────────────────────────────────

export interface V2Item {
  /** Unique item identifier (UUID or manual name). If absent / "auto", one is generated. */
  item: string;
  /** Internal runtime ID for tracking (added during preparation, removed before serialization) */
  id?: string;
  component: V2ComponentType;
  label?: V2LabelDef;
  unit?: V2UnitDef;
  function?: V2ButtonFunction; // for button components, the name of the function to call on click

  // ── Specific Component Properties ─────────────────────────────────────────
  /** Roles array used by the "users" component */
  roles?: string[];
  /** Property to extract from Yacht info for the "yachtData" component */
  yachtProp?: string;
  /** Additional features specific to item */
  features?: V2ItemFeatures;

  // ── Par component ─────────────────────────────────────────────────────────
  /** Used by component="par". List of paragraphs / links / images. */
  list?: V2ParEntry[];
  /** Layout direction for par component */
  layout?: "vertical" | "horizontal";

  // ── Convenience / defaults ────────────────────────────────────────────────
  /** Pre-filled / default value */
  value?: any;
  options?: Array<{ label: string; value: string } | string>;
  placeholder?: string;
  readonly?: boolean;
  required?: boolean;

  // ── Visual overrides ──────────────────────────────────────────────────────
  /** CSS outline */
  outline?: string;
  /** CSS width */
  width?: string;
  /** CSS class name(s) */
  class?: string;
  /** CSS style variant */
  style?: string;

  // ── component="label" shorthand ───────────────────────────────────────────
  // Some logbook defs (e.g. Draak) place label-display props directly on the
  // item when component="label" rather than inside a label sub-object.
  text?: string;
  textAlign?: "left" | "center" | "right";
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: string;
  padding?: string;
  orientation?: "top" | "left" | "right" | "bottom" | "none";
}

export interface V2ItemFeatures {
  /** Enable forward-dating for date/time inputs */
  forwardDating?: boolean;
  [key: string]: any;
}

// ─── Label ───────────────────────────────────────────────────────────────────

export interface V2LabelDef {
  text?: string;
  /** Text rendered inside the panel (if different from text) */
  panelText?: string;
  /** Descriptive subtitle shown beneath the label header */
  description?: string;

  textAlign?: "left" | "center" | "right";
  /** Where the label is placed relative to the input */
  orientation?: "top" | "left" | "right" | "bottom" | "none";
  /** CSS padding */
  padding?: string;

  /** Visual style of the label band */
  style?: "solid" | "subtle" | "none" | string;
  /** CSS color (text color) */
  color?: string;
  /** CSS background color */
  backColor?: string;
  /** CSS outline */
  outline?: string;

  bold?: boolean;
  italic?: boolean;
  underline?: boolean;

  /** Fixed width of the label */
  width?: string;
  fontSize?: string;

  /** Flexbox align-items for the label wrapper */
  align?: string;

  /** ID of the component that acts as date / entry selector for this group header */
  entrySelector?: string;
}

// ─── Unit / Suffix ───────────────────────────────────────────────────────────

export interface V2UnitDef {
  /** Suffix text */
  text?: string;

  // Both naming styles are in use across mock data and will be normalised at parse time.
  /** Width of the suffix box (canonical name) */
  unitWidth?: string;
  /** Visual style of the suffix box (canonical name) */
  unitStyle?: "solid" | "inset-solid" | "blank";

  // Short aliases used in some logbook definitions (Draak)
  /** Alias for unitWidth */
  width?: string;
  /** Alias for unitStyle */
  style?: "solid" | "inset-solid" | "blank" | string;
}

export type V2ButtonFunction = "moveRow" | "deleteRow" | "copyRow" | "addRow";

// ─── Inherited Styles ────────────────────────────────────────────────────────

/** Styles declared on a group that are applied to child items as defaults */
export interface V2InheritedDef {
  /** Inherited label defaults for child items */
  label?: Partial<V2LabelDef>;
  /** Inherited unit defaults for child items */
  unit?: Partial<V2UnitDef>;
  /** Inherited text-input defaults for child items (padding, width, etc.) */
  text?: V2InheritedTextDef;
}

/** CSS-style properties passed down to the underlying text input element */
export interface V2InheritedTextDef {
  padding?: string;
  width?: string;
  [key: string]: string | undefined;
}

// ─── Par Component Entries ───────────────────────────────────────────────────

/** A single entry inside a "par" (paragraph/rich-text) component list */
export type V2ParEntry = V2ParText | V2ParLink | V2ParImage;

export interface V2ParText {
  text: string;
  textAlign?: "left" | "center" | "right";
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

export interface V2ParLink {
  link: string; // visible link text
  url: string;
  target?: "_blank" | "_self" | string;
}

export interface V2ParImage {
  image: true;
  /** URL of the image, or a data-URI */
  url?: string;
  /** Inline base64 data-URI (alternative to url) */
  data?: string;
  width?: string;
  height?: string;
}

// ─── Component Types ─────────────────────────────────────────────────────────

export type V2ComponentType =
  | "text" // single-line text input
  | "textArea" // multi-line text input
  | "number" // numeric input
  | "list" // drop-down select
  | "check" // boolean checkbox
  | "time" // time picker
  | "date" // date picker
  | "datetime" // date + time picker
  | "signature" // signature pad
  | "gps" // GPS coordinate input
  | "yachtinfo" // yacht particulars block
  | "yachtData" // extracts single prop from yacht profile
  | "users" // users dropdown select
  | "par" // paragraph / rich-text display block
  | "label" // display-only text label
  | "divider" // horizontal rule
  | "spacer" // empty gap
  | "loclookup" // Location/Port lookup
  | "button"; // button

// ─── Type Guards ─────────────────────────────────────────────────────────────

/**
 * Type guard to check if a content node is a group.
 * @param node - Node to test
 * @returns True if node is V2Group
 */
export function isV2Group(node: V2ContentNode): node is V2Group {
  return "group" in node;
}

/**
 * Type guard to check if a content node is an item.
 * @param node - Node to test
 * @returns True if node is V2Item
 */
export function isV2Item(node: V2ContentNode): node is V2Item {
  return "item" in node;
}

// ─── Entry Value Store ───────────────────────────────────────────────────────

/** Runtime map of item-ID → current value */
export type V2EntryValues = Record<string, any>;
